import { useState } from "react";
import { useGetList } from "react-admin";
import { PageHeader } from "@/components/common/PageHeader";
import { Add } from "@mui/icons-material";
import { GhostButton } from "@/components/common/GhostButton";
import { CreateInDialog } from "@/components/common/CreateInDialog";
import {
  DocumentRead,
  DocumentRecordType,
  DropzoneDocumentType,
  ReimbursementCreate,
  ResourceName,
  TargetSchema,
} from "@/types";
import { DocumentQueue } from "@/resources/documents/components/DocumentQueue";
import { DocumentDropzone } from "@/components/common/DocumentDropzone";
import { getResourceIcon } from "@/utils/icons";
import { PageContent } from "@/components/common/PageLayout";
import { SectionCard } from "@/components/common/SectionCard";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";

export const ReimbursementsCreate: React.FC = () => {
  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  const StoplossClaimsIcon = getResourceIcon(ResourceName.REIMBURSEMENTS);

  const { canCreate: canCreateDocuments, canList: canListDocuments } = useResourcePermissions(ResourceName.DOCUMENTS);

  // Get reimbursement-specific processing queue
  const {
    data: documents = [] as DocumentRead[],
    isLoading: documentsLoading,
    refetch: refetchDocuments,
  } = useGetList(ResourceName.DOCUMENTS, {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "created_datetime", order: "DESC" },
  });

  const handleUploadComplete = () => {
    // Refresh documents to show new uploads
    void refetchDocuments();
  };

  return (
    <>
      <PageHeader
        customTitle="Create Reimbursement"
        customSubtitle="Upload your reimbursement documents to get started. You can assign different stop-loss carriers to each file."
        hideSectionTitle
        headerIcon={<StoplossClaimsIcon sx={{ fontSize: 28, color: "primary.main" }} />}
        rightContent={
          <GhostButton
            label="Create Manually"
            startIcon={<Add />}
            onClick={() => {
              setOpenCreateDialog(true);
            }}
          />
        }
      />
      {/* Reimbursement Upload Section */}
      <PageContent>
        {canCreateDocuments && (
          <SectionCard>
            <DocumentDropzone
              documentType={DropzoneDocumentType.REIMBURSEMENTS}
              targetSchema={TargetSchema.REIMBURSEMENT}
              onUploadComplete={handleUploadComplete}
            />
          </SectionCard>
        )}
        {/* Processing Queue Section */}
        {canListDocuments && (
          <SectionCard title="Processing Queue" subtitle="View any processing reimbursement uploads">
            <DocumentQueue
              documents={documents}
              isLoading={documentsLoading}
              onRefresh={refetchDocuments}
              recordType={DocumentRecordType.REIMBURSEMENTS}
            />
          </SectionCard>
        )}
      </PageContent>
      {/* Create Manual Dialog */}
      <CreateInDialog<ReimbursementCreate>
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        resource={ResourceName.REIMBURSEMENTS}
      />
    </>
  );
};
